import React, { useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';

import { Country } from 'ts/Country';
import { useValidation } from 'hooks';
import { useTranslation } from 'hooks/useTranslation';
import { InputField } from 'shared/forms';
import { SelectInputField } from 'features/app/components/SelectInputField';
import BaseButton from 'features/app/components/BaseButton';
import countries from 'features/dashboard/constants/countries.json';

import { useStyles } from './styles';

interface PersonalInformationStepProps {
  onSubmit: () => void;
}

export const PersonalInformationStep: React.FC<PersonalInformationStepProps> = ({ onSubmit }) => {

  const classes = useStyles();
  const { t } = useTranslation();
  const { required, email, phoneNumber } = useValidation();
  const [showPhoneNumberMessage, setShowPhoneNumberMessage] = useState(false);

  const mappedCountries = useMemo(
    () =>
      (countries as Country[]).map(({ code, name }, index) => ({
        id: index,
        code,
        label: name,
      })),
    []
  );

  const {
    control,
    trigger,
    formState: { errors },
  } = useFormContext();

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    if (!e.target.value?.includes('+')) setShowPhoneNumberMessage(!showPhoneNumberMessage);
    else setShowPhoneNumberMessage(false);
  };

  const handleSubmit = async () => {
    const isFormValid = await trigger([
      'email',
      'first_name',
      'last_name',
      'phone_number',
      'company',
      'designation',
      'country',
    ]);
    isFormValid && onSubmit();
  };

  return (
    <>
      <Box display="flex" justifyContent="space-between" paddingBottom={13}>
        <Box paddingRight={8} flexBasis={'50%'}>
          <InputField
            defaultValue=""
            required
            type="text"
            name="first_name"
            control={control}
            fullWidth
            placeholder={t('firstName', { ns: 'dashboard' })}
            label={t('firstName', { ns: 'dashboard' })}
            rules={{
              validate: {
                ...required,
              },
            }}
          />
        </Box>

        <Box paddingLeft={8} flexBasis={'50%'}>
          <InputField
            defaultValue=""
            required
            type="text"
            name="last_name"
            control={control}
            fullWidth
            placeholder={t('lastName', { ns: 'dashboard' })}
            label={t('lastName', { ns: 'dashboard' })}
            rules={{
              validate: {
                ...required,
              },
            }}
          />
        </Box>
      </Box>

      <InputField
        defaultValue=""
        required
        type="email"
        name="email"
        control={control}
        fullWidth
        placeholder={t('emailAddress', { ns: 'dashboard' })}
        label={t('emailAddress', { ns: 'dashboard' })}
        rules={{
          validate: {
            ...required,
            ...email,
          },
        }}
      />

      <Box className={classes.optionalContainer} padding={'15px 30px'} margin={'30px 0'}>
        <Typography className={classes.optionalText}>{t('optional', { ns: 'dashboard' })}</Typography>
      </Box>

      <Box display="flex" justifyContent="space-between" paddingBottom={13}>
        <Box paddingRight={8} flexBasis={'50%'}>
          <InputField
            defaultValue=""
            type="text"
            name="company"
            control={control}
            fullWidth
            placeholder={t('company', { ns: 'dashboard' })}
            label={t('company', { ns: 'dashboard' })}
          />
        </Box>

        <Box paddingLeft={8} flexBasis={'50%'}>
          <InputField
            defaultValue=""
            type="text"
            name="designation"
            control={control}
            fullWidth
            placeholder={t('designation', { ns: 'dashboard' })}
            label={t('designation', { ns: 'dashboard' })}
          />
        </Box>
      </Box>
      <Box display="flex" justifyContent="space-between" paddingBottom={13}>
        <Box paddingRight={8} flexBasis={'50%'}>
          <SelectInputField
            isClearable={true}
            name="country"
            label={t('country', { ns: 'dashboard' })}
            placeholder={t('selectFromList', { ns: 'dashboard' })}
            options={mappedCountries}
          />
        </Box>

        <Box paddingLeft={8} flexBasis={'50%'}>
          <InputField
            defaultValue=""
            type="text"
            name="phone_number"
            control={control}
            fullWidth
            onChange={handlePhoneNumberChange}
            placeholder={t('mobileNumber', { ns: 'dashboard' })}
            label={t('mobileNumber', { ns: 'dashboard' })}
            helperText={
              showPhoneNumberMessage
                ? t('phoneNumberValidationMessage', { ns: 'validation' })
                : errors?.phone_number?.message
            }
            rules={{
              validate: {
                phone_number: phoneNumber,
              },
            }}
          />
        </Box>
      </Box>

      <Grid container alignItems="center" justifyContent="center" className={classes.submitBtnContainer}>
        <BaseButton onClick={handleSubmit} maxWidth={269}>
          {t('next', { ns: 'dashboard' })}
        </BaseButton>
      </Grid>
    </>
  );
};
